import { useState } from 'react';
import { FileUploadSection } from './FileUploadSection';
import { PromptSelector } from './PromptSelector';
import { RefineOutput } from './RefineOutput';
import { UploadedFile } from './FileUpload';

export type PromptType = 'enrich' | 'format' | 'citation' | 'custom';

export interface RefinedOutput {
  id: string;
  version: number;
  markdown: string;
  tokensUsed: number;
  createdAt: string;
}

export interface RefineJob {
  jobId: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error?: string;
  outputs: RefinedOutput[];
}

export function RefineTab() {
  // File state
  const [noteFile, setNoteFile] = useState<UploadedFile | null>(null);
  const [sourceFile, setSourceFile] = useState<UploadedFile | null>(null);

  // Prompt state
  const [selectedPrompt, setSelectedPrompt] = useState<PromptType>('enrich');
  const [customPrompt, setCustomPrompt] = useState('');

  // Generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentJob, setCurrentJob] = useState<RefineJob | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canGenerate = noteFile !== null && (selectedPrompt !== 'custom' || customPrompt.trim().length > 0);

  const handleGenerate = async () => {
    if (!canGenerate || !noteFile) return;

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch('/api/refine/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          noteFileId: noteFile.fileId,
          sourceFileId: sourceFile?.fileId,
          promptType: selectedPrompt,
          customPrompt: selectedPrompt === 'custom' ? customPrompt : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Generation failed');
      }

      const data = await response.json();

      // If completed immediately
      if (data.status === 'completed' && data.output) {
        setCurrentJob({
          jobId: data.jobId,
          status: 'completed',
          outputs: [data.output],
        });
      } else {
        // Poll for status
        setCurrentJob({
          jobId: data.jobId,
          status: 'processing',
          outputs: [],
        });
        await pollJobStatus(data.jobId);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsGenerating(false);
    }
  };

  const pollJobStatus = async (jobId: string) => {
    const maxAttempts = 60; // 60 seconds max
    let attempts = 0;

    while (attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 1000));
      attempts++;

      try {
        const response = await fetch(`/api/refine/job/${jobId}`);
        if (!response.ok) continue;

        const data = await response.json();

        if (data.status === 'completed') {
          setCurrentJob({
            jobId,
            status: 'completed',
            outputs: data.outputs || [],
          });
          return;
        } else if (data.status === 'failed') {
          setError(data.error || 'Generation failed');
          setCurrentJob({
            jobId,
            status: 'failed',
            error: data.error,
            outputs: [],
          });
          return;
        }
      } catch {
        // Continue polling
      }
    }

    setError('Generation timed out. Please try again.');
  };

  const handleRegenerate = async () => {
    if (!currentJob) return;

    setIsGenerating(true);
    setError(null);

    try {
      const response = await fetch(`/api/refine/regenerate/${currentJob.jobId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || 'Regeneration failed');
      }

      const data = await response.json();

      setCurrentJob(prev => prev ? {
        ...prev,
        status: 'completed',
        outputs: [...prev.outputs, data.output],
      } : null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleReset = () => {
    setNoteFile(null);
    setSourceFile(null);
    setSelectedPrompt('enrich');
    setCustomPrompt('');
    setCurrentJob(null);
    setError(null);
  };

  // Get current step for visual indicator
  const getCurrentStep = () => {
    if (!noteFile) return 1;
    if (!currentJob) return 2;
    return 3;
  };

  const currentStep = getCurrentStep();

  return (
    <div className="space-y-8">
      {/* Progress Indicator */}
      <div className="flex items-center justify-center gap-4">
        {[1, 2, 3].map((step) => (
          <div key={step} className="flex items-center">
            <div
              className={`
                w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm transition-all
                ${step < currentStep ? 'bg-green-500 text-white' : ''}
                ${step === currentStep ? 'bg-primary-500 text-white ring-2 ring-primary-400/50 ring-offset-2 ring-offset-surface-900' : ''}
                ${step > currentStep ? 'bg-surface-700 text-surface-400' : ''}
              `}
            >
              {step < currentStep ? (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                step
              )}
            </div>
            {step < 3 && (
              <div
                className={`w-16 h-0.5 mx-2 transition-colors ${
                  step < currentStep ? 'bg-green-500' : 'bg-surface-700'
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step Labels */}
      <div className="flex justify-center gap-4 text-xs text-surface-400">
        <span className="w-24 text-center">Upload Files</span>
        <span className="w-24 text-center">Select Prompt</span>
        <span className="w-24 text-center">Generate</span>
      </div>

      {/* Main Content */}
      <div className="card p-6 space-y-8">
        {/* Step 1: File Upload */}
        <FileUploadSection
          noteFile={noteFile}
          sourceFile={sourceFile}
          onNoteFileChange={setNoteFile}
          onSourceFileChange={setSourceFile}
        />

        {/* Step 2: Prompt Selection */}
        {noteFile && (
          <div className="animate-fade-in">
            <PromptSelector
              selectedPrompt={selectedPrompt}
              customPrompt={customPrompt}
              onPromptChange={setSelectedPrompt}
              onCustomPromptChange={setCustomPrompt}
            />
          </div>
        )}

        {/* Generate Button */}
        {noteFile && !currentJob && (
          <div className="animate-fade-in flex justify-center pt-4">
            <button
              onClick={handleGenerate}
              disabled={!canGenerate || isGenerating}
              className="btn-primary px-8 py-4 text-lg flex items-center gap-3"
            >
              {isGenerating ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Transform My Notes
                </>
              )}
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="animate-fade-in bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-start gap-3">
            <svg className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="flex-1">
              <p className="text-red-300 font-medium">Generation Failed</p>
              <p className="text-red-400 text-sm mt-1">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-400 hover:text-red-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Step 3: Output Display */}
        {currentJob && (
          <div className="animate-fade-in">
            <RefineOutput
              job={currentJob}
              noteFileName={noteFile?.fileName || ''}
              sourceFileName={sourceFile?.fileName}
              promptType={selectedPrompt}
              isGenerating={isGenerating}
              onRegenerate={handleRegenerate}
              onReset={handleReset}
            />
          </div>
        )}
      </div>
    </div>
  );
}
